import { CampaignType, CampaignTemplate, IntervalType } from '@dev/ee-api/ee-api-models';
import { find } from 'lodash-es';
import { parse } from 'query-string';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { prepareAbSplitDataToDate, prepareAbSplitDataFromDate } from '@/components/Campaigns/helperFunctions';
import { LONG_DATE_FORMAT } from '@/services/constants';
import dayjs from '@/services/tools/dayjs';
import { ApplicationState } from '@/store';
import {
    GetCampaignClicksAndOpensByName,
    GetCampaignByName,
    GetCampaignReportsSummary,
    CancelPollingCampaignSimpleStats,
} from '@/store/campaigns/actions';
import { AbSplitDisplayMode } from '@/store/campaigns/types';
import { ListsLoadStart } from '@/store/lists/actions';
import { GetLinkTrackingListStart, ClearCampaignsReportsSummary } from '@/store/reports/actions';
import usePrevious from '@/services/hooks/usePrevious';

export const useComponentDidUpdate = (): void => {
    const { type } = useParams<{ name: string; type: CampaignType }>();
    const { campaigns } = useSelector((state: ApplicationState) => state);
    const { pickedCampaign, abSplitHeaderControls } = campaigns;
    const { campaign } = pickedCampaign;
    const { currentTemplate, displayMode: currentDisplayMode } = abSplitHeaderControls;

    const prevTemplate = usePrevious(currentTemplate);
    const prevDisplayMode = usePrevious(currentDisplayMode);

    useEffect(() => {
        if (type === CampaignType.AbSplit && campaign) {
            if (prevTemplate !== currentTemplate) {
                GetLinkTrackingListStart({
                    channelName: currentTemplate,
                    from: campaign?.TriggerOptions?.DateToSendOn,
                    to: prepareAbSplitDataToDate(campaign, currentDisplayMode),
                    activePage: 1,
                    perPage: 4,
                    isRequiredFullDateRange: false,
                });
            }

            if (prevDisplayMode !== currentDisplayMode) {
                const abSplitDataFromDate = prepareAbSplitDataFromDate(campaign, currentDisplayMode);
                const winnerTemplate: CampaignTemplate = find(
                    campaign.CampaignTemplates,
                    (template) => template.IsWinner
                );

                GetCampaignClicksAndOpensByName({
                    interval: IntervalType.Hourly,
                    channelName: campaign.Name,
                    from: abSplitDataFromDate,
                    to: dayjs(abSplitDataFromDate).add(48, 'h').format(LONG_DATE_FORMAT),
                });

                GetLinkTrackingListStart({
                    channelName:
                        currentDisplayMode === AbSplitDisplayMode.Winner ? winnerTemplate.Name : currentTemplate,
                    from: abSplitDataFromDate,
                    to: prepareAbSplitDataToDate(campaign, currentDisplayMode),
                    activePage: 1,
                    perPage: 4,
                    isRequiredFullDateRange: false,
                });
            }
        }
    }, [currentDisplayMode, currentTemplate]);
};

export const useComponentDidMountOrWillUnmount = (): void => {
    const { name, type } = useParams<{ name: string; type: CampaignType }>();
    const { campaigns } = useSelector((state: ApplicationState) => state);
    const { pickedCampaign, abSplitHeaderControls } = campaigns;
    const dispatch = useDispatch();
    const { campaign } = pickedCampaign;
    const { displayMode, currentTemplate } = abSplitHeaderControls;
    const decodedName = safelyDecodeBase64Url(name);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(
            GetCampaignByName({
                name: decodedName,
                campaignType: type,
                isLoadReportsRequired: type === CampaignType.SendNow,
                isSimpleStatsRequired: type === CampaignType.AbSplit,
                isLoadLinkTrackingRequired: type !== CampaignType.AbSplit,
                isAbSplitClickAndOpenTestResultsRequired: type === CampaignType.AbSplit,
                abSplitOpenAndClickTestResultsOptions: {
                    minutesFromStart: 48 * 60,
                },
            })
        );
        dispatch(ListsLoadStart({ limit: 0, offset: 0, modifyQueryString: false }));

        if (campaign && type === CampaignType.AbSplit) {
            const abSplitDataFromDate = prepareAbSplitDataFromDate(campaign, displayMode);
            const winnerTemplate: CampaignTemplate = find(
                campaign.CampaignTemplates,
                (template: CampaignTemplate) => template.IsWinner
            );

            dispatch(
                GetCampaignClicksAndOpensByName({
                    interval: IntervalType.Hourly,
                    channelName: campaign.Name,
                    from: abSplitDataFromDate,
                    to: dayjs(abSplitDataFromDate).add(48, 'h').format(LONG_DATE_FORMAT),
                })
            );

            dispatch(
                GetLinkTrackingListStart({
                    channelName: displayMode === AbSplitDisplayMode.Winner ? winnerTemplate.Name : currentTemplate,
                    from: abSplitDataFromDate,
                    to: prepareAbSplitDataToDate(campaign, displayMode),
                    activePage: 1,
                    perPage: 4,
                    isRequiredFullDateRange: false,
                })
            );
        }

        if (type === CampaignType.Automated) {
            const query = parse(location.search, { arrayFormat: 'comma' });
            const from = query.from
                ? dayjs(query.from as string).format(LONG_DATE_FORMAT)
                : dayjs().subtract(7, 'day').format(LONG_DATE_FORMAT);
            const to = query.to ? dayjs(query.to as string).format(LONG_DATE_FORMAT) : dayjs().format(LONG_DATE_FORMAT);

            dispatch(
                GetCampaignReportsSummary({
                    campaignName: decodedName,
                    type,
                    from,
                    to,
                    interval: IntervalType.Summary,
                })
            );
        }

        return () => {
            dispatch(CancelPollingCampaignSimpleStats());
            dispatch(ClearCampaignsReportsSummary());
        };
    }, []);
};
